import { ZipcodeConfig } from '@src/config/localisation/statsig-locale-config.types'

/**
 * Applies uppercase transformation if configured
 * @param value - The input value to transform
 * @param toUpperCase - Whether to convert to uppercase
 * @returns Transformed value
 */
export function applyUpperCaseFormatting(value: string, toUpperCase: boolean): string {
  return toUpperCase ? value.toUpperCase() : value
}

/**
 * Applies space formatting at specified position
 * @param value - The cleaned input value (no spaces)
 * @param spacePosition - Position to insert space (null if no space)
 * @param maxLength - Maximum length of the zipcode
 * @returns Formatted value with space inserted
 */
export function applySpaceFormatting(value: string, spacePosition: number | null, maxLength: number): string {
  if (!spacePosition || spacePosition === null) {
    return value
  }

  const maxCharsBeforeSpace = spacePosition
  const maxCharsAfterSpace = maxLength - spacePosition - 1 // -1 for space

  // Limit to maxLength (accounting for space)
  const limited = value.slice(0, maxCharsBeforeSpace + maxCharsAfterSpace)

  // Format: add space at configured position when enough characters entered
  if (limited.length > spacePosition) {
    return `${limited.slice(0, spacePosition)} ${limited.slice(spacePosition)}`
  }

  return limited
}

/**
 * Inserts space before the last N characters (e.g. UK: last 3 = incode "1AA").
 * Use when postcode has variable-length outcode (UK: 2–4 chars) so one config fits all.
 * @param value - The cleaned input value (no spaces)
 * @param spaceBeforeLastN - Insert space before this many trailing characters
 * @param maxLength - Maximum length of the zipcode (including space)
 * @returns Formatted value with space inserted before last N chars
 */
export function applySpaceBeforeLastN(value: string, spaceBeforeLastN: number, maxLength: number): string {
  if (spaceBeforeLastN <= 0) return value

  // Limit so final string (with space) has length <= maxLength → at most maxLength - 1 chars before inserting space
  const limited = value.slice(0, maxLength - 1)
  if (limited.length <= spaceBeforeLastN) return limited

  const splitAt = limited.length - spaceBeforeLastN
  return `${limited.slice(0, splitAt)} ${limited.slice(splitAt)}`
}

/**
 * Solves postcode ambiguity by finding the unique valid split point.
 * Checks all possible splits against valid Outcode and Incode-Start patterns.
 * If exactly one split is valid, it formats the string.
 *
 * @param value - Cleaned alphanumeric value
 * @param outcodePattern - Regex pattern for valid Outcode
 * @param incodePattern - Regex pattern for valid Incode prefix
 * @returns Formatted value (with space) or original value (if ambiguous/invalid)
 */
export function solveAmbiguousSplit(value: string, outcodePattern: RegExp, incodePattern: RegExp): string {
  // Need at least 2 characters to have a split (1 char outcode + 1 char incode)
  if (value.length < 2) return value

  const validSplits: number[] = []

  // Iterate all possible splits from index 1 to length-1
  // We let the RegExp patterns decide what constitutes a valid length for outcode/incode
  for (let i = 1; i < value.length; i++) {
    const prefix = value.slice(0, i)
    const suffix = value.slice(i)

    if (outcodePattern.test(prefix) && incodePattern.test(suffix)) {
      validSplits.push(i)
    }
  }

  // Also check if the FULL string is a valid Outcode itself (e.g. "M11", "SW1A")
  // If so, that is a valid interpretation (i.e. "User is still typing Outcode")
  const isFullOutcode = outcodePattern.test(value)
  const totalInterpretations = validSplits.length + (isFullOutcode ? 1 : 0)

  // If exactly one valid interpretation exists, and it is a SPLIT, then Format!
  // If the unique interpretation is "Full Outcode", we do NOT format (wait for Incode start).
  // If multiple interpretations (Ambiguous), we do NOT format.
  if (totalInterpretations === 1 && validSplits.length === 1) {
    const splitAt = validSplits[0]
    return `${value.slice(0, splitAt)} ${value.slice(splitAt)}`
  }

  return value
}

/**
 * Formats zipcode input from config only. No country or locale awareness – behaviour is entirely driven by config
 * (from Statsig or locale config). Handles space formatting, uppercase, and length limiting.

 *
 * @param value - The raw input value from the user
 * @param config - Zipcode configuration (Statsig locale config or default); if missing, returns value unchanged
 * @returns Formatted zipcode value
 */
export function formatZipCodeInput(value: string, config?: ZipcodeConfig | null): string {
  if (!config) return value

  const maxLength = config?.maxLength ?? 10
  const formatConfig = config?.format

  // Treat '' (Statsig can't store null) the same as null/undefined — only positive numbers are "set"
  const spaceBeforeLastN =
    typeof formatConfig?.spaceBeforeLastN === 'number' && formatConfig.spaceBeforeLastN > 0
      ? formatConfig.spaceBeforeLastN
      : null
  const spacePosition =
    typeof formatConfig?.spacePosition === 'number' && formatConfig.spacePosition > 0
      ? formatConfig.spacePosition
      : null

  if (formatConfig?.hasSpace && (spaceBeforeLastN != null || spacePosition != null)) {
    let cleaned = value.replace(/\s/g, '')
    cleaned = applyUpperCaseFormatting(cleaned, formatConfig?.toUpperCase ?? false)
    const alphanumericOnly = cleaned.replace(/[^A-Z0-9]/g, '')

    // When spaceBeforeLastN is set, use only that (variable-length outcode, e.g. UK). Otherwise use spacePosition.
    if (spaceBeforeLastN != null) {
      const maxRawLength = maxLength - 1 // max characters without the space

      // 1. Complete input → always format with correct space position (Standard logic)
      if (alphanumericOnly.length >= maxRawLength) {
        return applySpaceBeforeLastN(alphanumericOnly, spaceBeforeLastN, maxLength)
      }

      // 2. Short/Partial input (Variable Length Outcodes) → Try Structural Ambiguity Solver
      // Instead of hardcoding for UK (spaceBeforeLastN=3), we check if the config provides regex logic for splitting.
      const outcodeRegexStr = formatConfig?.outcodeRegex
      const incodePrefixRegexStr = formatConfig?.incodePrefixRegex

      if (outcodeRegexStr && incodePrefixRegexStr) {
        try {
          // eslint-disable-next-line i18next/no-literal-string
          const outcodePattern = new RegExp(outcodeRegexStr, 'i')
          // eslint-disable-next-line i18next/no-literal-string
          const incodePattern = new RegExp(incodePrefixRegexStr, 'i')

          const smartFormatted = solveAmbiguousSplit(alphanumericOnly, outcodePattern, incodePattern)
          // If helper returned a formatted string (contains space), use it
          if (smartFormatted !== alphanumericOnly) {
            return smartFormatted
          }
        } catch (e) {
          // Fallback if regex is invalid
          console.warn('Invalid regex in zipcode config', e)
        }
      }

      // If the value already had a space (user is editing/deleting, not typing fresh),
      // preserve the outcode/incode boundary so the space doesn't disappear on delete.
      const existingSpaceIndex = value.indexOf(' ')
      if (existingSpaceIndex > 0 && existingSpaceIndex < alphanumericOnly.length) {
        const outcode = alphanumericOnly.slice(0, existingSpaceIndex)
        const incode = alphanumericOnly.slice(existingSpaceIndex)
        return `${outcode} ${incode}`
      }

      // Fresh typing (no space in value yet) and Ambiguous/Too Short: just uppercase + limit length
      return alphanumericOnly.slice(0, maxRawLength)
    }
    return applySpaceFormatting(alphanumericOnly, spacePosition, maxLength)
  }

  // No space formatting - just apply uppercase if configured
  let formatted = value
  if (formatConfig?.toUpperCase) {
    formatted = applyUpperCaseFormatting(formatted, formatConfig.toUpperCase)
  }

  // Limit to maxLength
  return formatted.slice(0, maxLength)
}
